<template>
  <Section
    :id="id"
    :aria-label="
      t('common.paramConfigOptions', { param: t('shipments.tender') })
    "
    class="tender-config-options-section"
    data-class="flex flex-column flex-gap-1"
  >
    <fieldset
      v-if="showExpirationAndNotesInput"
      :id="`${id}-tender-specific-options`"
    >
      <legend class="visually-hidden">
        {{ t('common.paramConfigOptions', { param: tenderMethodString }) }}
      </legend>

      <InputWrapper
        v-if="tenderMethodFlags.isEmail"
        v-slot="{ ariaProps, inputId, invalid }"
        :input-id="`${id}-emails`"
        :errors="v$.emails.$errors"
        :label="t('shipments.emailsCommaSeparated')"
      >
        <InputText
          :id="inputId"
          v-model="tenderConfigOptions.emails"
          v-bind="ariaProps"
          :invalid="invalid"
          :placeholder="t('shipments.emailsCommaSeparated')"
          :disabled="emailDisabled"
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ inputId }"
        :input-id="`${id}-expiration`"
        :label="t('common.expiration')"
        optional
      >
        <RygenDropdown
          :id="inputId"
          v-model="tenderConfigOptions.expiration"
          :options="options"
          :placeholder="t('common.default')"
          append-to="body"
          option-label="label"
          option-value="value"
          show-clear
        />
      </InputWrapper>

      <InputWrapper
        v-slot="{ ariaProps, inputId }"
        :input-id="`${id}-notes`"
        :label="t('orders.notes')"
        optional
      >
        <Textarea
          :id="inputId"
          v-model="tenderConfigOptions.notes"
          v-bind="ariaProps"
          :placeholder="t('orders.notes')"
        />
      </InputWrapper>
    </fieldset>

    <ParcelConfigOptions
      v-else-if="showParcelOptions"
      :id="`${id}-parcel-options`"
      v-model="tenderConfigOptions"
    />

    <p v-if="tenderMethodFlags.isManual" class="tender-method-message">
      {{ t('shipments.confirmTenderManual') }}
    </p>
    <p v-else-if="tenderMethodFlags.isP44" class="tender-method-message">
      {{ t('shipments.confirmTenderP44') }}
    </p>
    <p
      v-else-if="tenderMethodFlags.isApi && !isParcelRate"
      class="tender-method-message"
    >
      {{ t('shipments.confirmTenderAPI') }}
    </p>
    <template v-else>
      <p class="tender-method-message">
        {{
          t('rateShop.assignAndTenderModal.paramBookedAndScheduled', {
            param: isOrderInstance ? 'Order' : 'Shipment',
            type: isBrokerageQuote
              ? t('brokerages.brokers')
              : t('carriers.carriers'),
          })
        }}
      </p>
      <p class="tender-method-message">
        {{ t('rateShop.assignAndTenderModal.selectTenderOrCancel') }}
      </p>
    </template>
  </Section>
</template>

<script lang="ts" setup>
import { useFlag } from '@unleash/proxy-client-vue';
import { ValidationRule } from '@vuelidate/core';
import type { PropType } from 'vue';
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  matchesEmail,
  required,
  useValidations,
  ValidationRules,
} from '@rygen/rygen-ui/composables';
import {
  mapEnumToTranslatedDropdownValues,
  translateEnum,
} from '@rygen/rygen-ui/i18n';
import {
  DropdownOption,
  ExpirationDuration,
  Mode,
  TenderConfigOptions,
  TenderMethod,
} from '@rygen/rygen-ui/model';

import { useCorsairPermissions } from '@/composables';
import { Order, RateQuote } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import ParcelConfigOptions from '@/components/rateShop/assignAndTender/ParcelConfigOptions.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import RygenDropdown from '@rygen/rygen-ui/components/form/RygenDropdown.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';

const tenderConfigOptions = defineModel({
  type: TenderConfigOptions,
  required: true,
});

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  rateQuote: {
    type: RateQuote,
    required: true,
  },
  selectedTenderMethod: {
    type: String as PropType<TenderMethod>,
    default: () => TenderMethod.MANUAL,
  },
  isTenderOnly: {
    type: Boolean,
    default: false,
  },
});
const { id, rateQuote, selectedTenderMethod } = toRefs(props);

const { shouldDisableTenderEmail } = useCorsairPermissions();
const { t } = useI18n();
const { parentEntity } = useParentEntityStore();
const isParcelTenderingEnabled = useFlag('parcelTendering');

const options: DropdownOption<string>[] = mapEnumToTranslatedDropdownValues(
  ExpirationDuration,
  'ExpirationDuration',
);

const isParcelRate = computed(() => rateQuote.value.mode === Mode.PARCEL);

const tenderMethodFlags = computed(() => {
  return {
    isApi: selectedTenderMethod.value === TenderMethod.API,
    isEmail: selectedTenderMethod.value === TenderMethod.EMAIL,
    isEdi: selectedTenderMethod.value === TenderMethod.EDI,
    isManual: selectedTenderMethod.value === TenderMethod.MANUAL,
    isP44:
      selectedTenderMethod.value === TenderMethod.P44 ||
      selectedTenderMethod.value === TenderMethod.P44_VOLUME_LTL,
  };
});

const rules = computed((): ValidationRules => {
  const validations: Record<string, Record<string, ValidationRule>> = {
    emails: {},
  };

  if (tenderMethodFlags.value.isEmail) {
    validations.emails.required = required(t('common.email'));
    validations.emails.matches = matchesEmail();
  }

  return validations;
});
const { v$ } = useValidations(tenderConfigOptions, rules);

const isBrokerageQuote = computed(() => rateQuote.value.source === 'BROKERAGE');

const tenderMethodString = computed(() =>
  translateEnum(selectedTenderMethod.value, 'TenderMethod'),
);

const isOrderInstance = computed(
  (): boolean => parentEntity.value instanceof Order,
);

const showExpirationAndNotesInput = computed(
  () => tenderMethodFlags.value.isEdi || tenderMethodFlags.value.isEmail,
);

const showParcelOptions = computed(
  () =>
    isParcelTenderingEnabled.value &&
    isParcelRate.value &&
    tenderMethodFlags.value.isApi,
);

const emailDisabled = computed(() => {
  return shouldDisableTenderEmail(rateQuote.value.carrier?.type);
});
</script>

<style>
.tender-config-options-section {
  fieldset {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);
    margin: 0;
    padding: 0;
    border: none;
  }
}
</style>
